import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Callout, EmptyState, Filter, Link, Paginator, Spinner, Table, Tag, type FilterOption, type TableColumn } from '@humanforce/mango-ui-react';
import type { PayRunExceptionItem, PayRunExceptionsPage, PopulationLoadStatus } from './payRunPopulationApiTypes';
import { actionPayRunComplianceFinding, getPayRunExceptions } from './payRunPopulationHttpClient';

interface PayRunExceptionsTabProps {
  payId: string;
  populationStatus: PopulationLoadStatus | null | undefined;
}

const pageSize = 20;
const words = (value: string): string => value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
const lifecycleOptions: FilterOption[] = [
  { value: 'OPEN', label: 'Open' },
  { value: 'ACTIONED', label: 'Actioned' },
  { value: 'RESOLVED', label: 'Resolved' },
];

export const PayRunExceptionsTab = ({ payId, populationStatus }: PayRunExceptionsTabProps) => {
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<PayRunExceptionsPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actioningId, setActioningId] = useState<string | null>(null);
  const [lifecycle, setLifecycle] = useState('');

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      setResult(await getPayRunExceptions(payId, { page, pageSize, lifecycle: lifecycle || undefined }, signal));
    } catch (loadError) {
      if (!signal?.aborted) setError(loadError instanceof Error ? loadError.message : 'Exceptions could not be loaded.');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [lifecycle, page, payId]);

  const actionFinding = useCallback(async (findingId: string) => {
    setActioningId(findingId);
    setError(null);
    try {
      await actionPayRunComplianceFinding(payId, findingId);
      await load();
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : 'The finding could not be actioned.');
    } finally {
      setActioningId(null);
    }
  }, [load, payId]);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load, populationStatus]);

  const columns = useMemo<TableColumn[]>(() => [
    {
      field: 'code',
      header: 'Exception',
      body: (row: PayRunExceptionItem) => `${row.displayName ?? words(row.code)} — ${row.message}`,
    },
    { field: 'origin', header: 'Origin', body: (row: PayRunExceptionItem) => words(row.origin) },
    {
      field: 'severity',
      header: 'Severity',
      body: (row: PayRunExceptionItem) => words(row.severity),
    },
    { field: 'workEntityId', header: 'Worker', body: (row: PayRunExceptionItem) => row.personId
      ? <Link href={`/workers/worker-record/personal?worker=${encodeURIComponent(row.personId)}`}>Open worker</Link>
      : row.workEntityId ?? 'Pay Run' },
    { field: 'lifecycle', header: 'Lifecycle', body: (row: PayRunExceptionItem) => row.lifecycle ? words(row.lifecycle) : 'Current input issue' },
    {
      field: 'actions',
      header: 'Actions',
      body: (row: PayRunExceptionItem) => row.origin === 'compliance' && row.lifecycle === 'OPEN'
        ? <Button label="Mark actioned" variant="secondary" size="small" disabled={actioningId === row.id} onClick={() => void actionFinding(row.id)} />
        : '—',
    },
  ], [actionFinding, actioningId]);

  const totalPages = result ? Math.ceil(result.totalCount / result.pageSize) : 0;
  return (
    <section className="mgo-stack mgo-gap-l" aria-label="Pay Run compliance" aria-busy={loading}>
      <Filter type="single-select" name="Lifecycle" options={lifecycleOptions} value={lifecycle || null}
        onChange={(value) => { setPage(1); setLifecycle(value == null ? '' : String(value)); }} />
      {result?.summary.blocking ? (
        <Callout tone="critical" title="Cannot start pay">
          {result.summary.blocking} blocking {result.summary.blocking === 1 ? 'finding must' : 'findings must'} be resolved before this Pay Run can progress.
        </Callout>
      ) : null}
      {result && (
        <div className="mgo-cluster mgo-gap-s" aria-label="Exception summary">
          <Tag label={`${result.summary.blocking} blocking`} tone={result.summary.blocking ? 'critical' : 'neutral'} />
          <Tag label={`${result.summary.warnings} warnings`} tone={result.summary.warnings ? 'caution' : 'neutral'} />
          <Tag label={`${result.summary.reviewRequired} review required`} tone={result.summary.reviewRequired ? 'caution' : 'neutral'} />
          <span className="mgo-text-xs mgo-color-muted">{result.summary.affectedPeople} affected people</span>
        </div>
      )}
      {result?.isStale && <Callout tone="caution" title="Compliance evaluation required">Run compliance evaluation to refresh canonical findings.</Callout>}
      {populationStatus === 'loading' && <Callout tone="info" title="Refreshing exceptions">Results update when the employee snapshot is ready.</Callout>}
      {error && <Callout tone="critical" title="Exceptions unavailable" action={<Button label="Retry" variant="secondary" size="small" onClick={() => void load()} />}>{error}</Callout>}
      {loading && !error ? (
        <div className="mgo-cluster mgo-justify-center mgo-gap-s" style={{ minHeight: '12rem' }}><Spinner /><span>Loading exceptions…</span></div>
      ) : !error && result?.items.length === 0 ? (
        <EmptyState variant="success" title="No current exceptions" description="No canonical findings or outstanding input review issues were found." />
      ) : !error && result ? (
        <Table value={result.items} columns={columns} scrollable emptyMessage="No exceptions." testId="pay-run-exceptions-table" />
      ) : null}
      {!loading && !error && result && totalPages > 1 && <Paginator first={(page - 1) * pageSize}
        itemsPerPage={pageSize} totalRecords={result.totalCount}
        onPageChange={(event) => setPage(event.page + 1)} />}
    </section>
  );
};
